import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Stack,
  TableHead,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormikContext } from 'formik';
import {
  CustomTable,
  CustomTableBody,
  CustomTableCell,
  CustomTableContainer,
  CustomTableHeadCell,
  CustomTableRow,
} from 'src/components/MainPage/_shared/Table';
import { EditableNumberCell } from 'src/components/MainPage/_shared/Table/EditableNumberCell';
import { TextTruncated } from 'src/uikit/TextTruncated';
import { useUnitOfMeasurements } from '../../shared/useUnitOfMeasurements';
import {
  INCOME_ITEM_COLUMNS,
  ITEM_NAME_CELL_WIDTH,
  TABLE_STYLES,
  MAX_COMMENT_LENGTH,
} from '../constants';
import { AddItemForm } from './AddItemForm';
import { EditableTextCell } from 'src/components/MainPage/_shared/Table/EditableTextCell';
import {
  getUnitName,
  getItemTotalPrice,
  formatPrice,
} from '../../WarehouseIncomesPage/helpers';
import {
  WarehouseIncomeValues,
  WarehouseIncomeItemValues,
} from '../../WarehouseIncomesPage/services';
import { NoItemsCell } from './NoItemsCell';
import {
  CommentCell,
  CommentPopover,
  useCommentPopover,
} from 'src/components/MainPage/_shared/Comment';

type Props = {
  disabled?: boolean;
};

export const ItemsEditableTable = ({ disabled = false }: Props) => {
  const { values, setFieldValue } = useFormikContext<WarehouseIncomeValues>();
  const [editingCellId, setEditingCellId] = useState<string | null>(null);

  const items = useMemo(() => values.items ?? [], [values.items]);

  const { unitsMap } = useUnitOfMeasurements();

  const handleAdd = (item: WarehouseIncomeItemValues) => {
    setFieldValue('items', [item, ...items]);
  };

  const handleDelete = (itemId: string) => {
    setFieldValue(
      'items',
      items.filter((item) => item.id !== itemId)
    );
  };

  const handleNumberChange = (
    itemId: string,
    field: 'quantity' | 'purchasePrice',
    value: number | null
  ) => {
    setFieldValue(
      'items',
      items.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  const handleBatchChange = (itemId: string, value: string) => {
    setFieldValue(
      'items',
      items.map((item) =>
        item.id === itemId ? { ...item, batchNumber: value } : item
      )
    );
  };

  const totalSum = useMemo(
    () => items.reduce((sum, item) => sum + getItemTotalPrice(item), 0),
    [items]
  );

  const {
    anchorEl,
    itemId: commentItemId,
    isOpen,
    currentComment,
    openComment,
    closeComment,
    getCommentTooltip,
  } = useCommentPopover(items);

  const handleCommentChange = (itemId: string, value: string) => {
    setFieldValue(
      'items',
      items.map((item) =>
        item.id === itemId ? { ...item, comment: value } : item
      )
    );
  };

  return (
    <Stack flex={1}>
      <AddItemForm onAdd={handleAdd} disabled={disabled} />

      <CustomTableContainer
        data-testid="warehouse-income-editable-items_table"
        sx={{ height: 'auto' }}
      >
        <CustomTable stickyHeader fullHeight={!items.length} sx={TABLE_STYLES}>
          {items.length > 0 && (
            <TableHead>
              <CustomTableRow withDivider keepSticky>
                {INCOME_ITEM_COLUMNS.map(({ key, label }, index) => (
                  <CustomTableHeadCell
                    key={key}
                    colSpan={index === INCOME_ITEM_COLUMNS.length - 1 ? 2 : 1}
                  >
                    {label}
                  </CustomTableHeadCell>
                ))}
              </CustomTableRow>
            </TableHead>
          )}

          <CustomTableBody>
            {items.map((item) => {
              const unitName = getUnitName(
                unitsMap,
                item.nomenclatureUnitOfMeasurement
              );
              const total = getItemTotalPrice(item);

              return (
                <CustomTableRow key={item.id}>
                  <CustomTableCell className="name-cell">
                    <Stack direction="row" alignItems="center" gap={1}>
                      <TextTruncated
                        text={item.nomenclatureName || '—'}
                        maxWidth={ITEM_NAME_CELL_WIDTH}
                      />
                      {unitName && (
                        <Typography variant="body2" color="text.secondary">
                          ({unitName})
                        </Typography>
                      )}
                    </Stack>
                  </CustomTableCell>

                  <EditableNumberCell
                    value={item.quantity}
                    onSave={(val) => {
                      handleNumberChange(item.id!, 'quantity', val);
                      setEditingCellId(null);
                    }}
                    onStartEditing={() =>
                      setEditingCellId(`${item.id}:quantity`)
                    }
                    onStopEditing={() => setEditingCellId(null)}
                    isEditing={editingCellId === `${item.id}:quantity`}
                    disabled={disabled}
                  />

                  <EditableNumberCell
                    value={item.purchasePrice}
                    onSave={(val) => {
                      handleNumberChange(item.id!, 'purchasePrice', val);
                      setEditingCellId(null);
                    }}
                    onStartEditing={() =>
                      setEditingCellId(`${item.id}:purchasePrice`)
                    }
                    onStopEditing={() => setEditingCellId(null)}
                    isEditing={editingCellId === `${item.id}:purchasePrice`}
                    disabled={disabled}
                  />

                  <CustomTableCell>{formatPrice(total)}</CustomTableCell>

                  <EditableTextCell
                    value={item.batchNumber ?? ''}
                    onSave={(val) => {
                      handleBatchChange(item.id!, val);
                      setEditingCellId(null);
                    }}
                    onStartEditing={() =>
                      setEditingCellId(`${item.id}:batchNumber`)
                    }
                    onStopEditing={() => setEditingCellId(null)}
                    isEditing={editingCellId === `${item.id}:batchNumber`}
                    disabled={disabled}
                    placeholder="Партия"
                  />

                  <CustomTableCell>
                    <CommentCell
                      comment={item.comment}
                      isReadOnly={disabled}
                      tooltip={getCommentTooltip(item.comment, disabled)}
                      onOpen={(e) => openComment(e, item.id!)}
                    />
                  </CustomTableCell>

                  <CustomTableCell align="center">
                    <Tooltip title="Удалить">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item.id!)}
                        disabled={disabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CustomTableCell>
                </CustomTableRow>
              );
            })}
            {items.length === 0 && (
              <NoItemsCell
                colSpan={INCOME_ITEM_COLUMNS.length + 1}
                text={'Номенклатуры не добавлены'}
              />
            )}
          </CustomTableBody>
        </CustomTable>
      </CustomTableContainer>

      {items.length > 0 && (
        <Box sx={{ padding: '0.75rem 1.25rem' }}>
          <Typography variant="h6">
            <strong>Итого:</strong> {formatPrice(totalSum)}
          </Typography>
        </Box>
      )}
      <CommentPopover
        open={isOpen}
        anchorEl={anchorEl}
        comment={currentComment}
        onSave={(draft) => {
          if (commentItemId) handleCommentChange(commentItemId, draft);
        }}
        onDelete={() => {
          if (commentItemId) handleCommentChange(commentItemId, '');
        }}
        onClose={closeComment}
        isReadOnly={disabled}
        maxLength={MAX_COMMENT_LENGTH}
      />
    </Stack>
  );
};
